import { createContext, useContext, useState } from "react";
import {
  SEND_APPROVER_EMAIL_NOTIFY_FOR_SELECTED_REPORT_API,
  GET_APPROVAL_FLOW_API,
  SAVE_APPROVAL_DATA_API,
} from "../config/apiConfig";
import { securedFetch } from "../utils/api";

const ApprovalContext = createContext();

const buildApprovalFlowKey = ({
  region,
  businessEntityID,
  measure,
  dimension,
  dimension2,
  isGlobal,
  isModelerReport,
  isTreasuryKRIReport,
  isKRISummaryReport,
  isSFCKRIGlobalSummaryReport,
  isRegionKRIGlobalSummaryReport,
  isTFSCKRIGlobalSummaryReport,
}) => {
  return [
    region ?? "null",
    businessEntityID ?? "null",
    measure ?? "null",
    dimension ?? "null",
    dimension2 ?? "null",
    isGlobal ?? "null",
    isModelerReport ?? "null",
    isTreasuryKRIReport ?? "null",
    isKRISummaryReport ?? "null",
    isSFCKRIGlobalSummaryReport ?? "null",
    isRegionKRIGlobalSummaryReport ?? "null",
    isTFSCKRIGlobalSummaryReport ?? "null",
  ].join("|");
};

export const ApprovalProvider = ({ children }) => {
  const [approvalFlowLoading, setApprovalFlowLoading] = useState(false);
  const [approvalFlowError, setApprovalFlowError] = useState(null);
  const [approvalFlow, setApprovalFlow] = useState([]);

  const [approvalFlowByKey, setApprovalFlowByKey] = useState({});
  const [approvalFlowLoadingByKey, setApprovalFlowLoadingByKey] = useState({});
  const [approvalFlowErrorByKey, setApprovalFlowErrorByKey] = useState({});

  const [saveApprovalLoading, setSaveApprovalLoading] = useState(false);
  const [saveApprovalError, setSaveApprovalError] = useState(null);
  const [saveApproval, setSaveApproval] = useState(null);

  const [sendEmailNotifyLoading, setSendEmailNotifyLoading] = useState(false);
  const [sendEmailNotifyError, setSendEmailNotifyError] = useState(null);
  const [sendEmailNotify, setSendEmailNotify] = useState(null);

  const fetchApprovalFlowData = async ({
    region,
    businessEntityID,
    selectedReportDate,
    measure,
    dimension,
    dimension2,
    isGlobal,
    isModelerReport,
    isTreasuryKRIReport,
    isKRISummaryReport,
    isSFCKRIGlobalSummaryReport,
    isRegionKRIGlobalSummaryReport,
    isTFSCKRIGlobalSummaryReport,
  } = {}) => {
    setApprovalFlowLoading(true);
    setApprovalFlowError(null);
    try {
      const response = await securedFetch(GET_APPROVAL_FLOW_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          region,
          businessEntityID,
          reportDate: selectedReportDate?.format("YYYY-MM-DD"),
          measure,
          dimension,
          dimension2,
          isGlobal,
          isModelerReport,
          isTreasuryKRIReport,
          isKRISummaryReport,
          isSFCKRIGlobalSummaryReport,
          isRegionKRIGlobalSummaryReport,
          isTFSCKRIGlobalSummaryReport,
        }),
      });

      if (!response.ok) throw new Error("Failed to fetch the approval flow");
      const data = await response.json();
      if (!data) throw new Error("Failed to fetch the approval flow");

      setApprovalFlow(data);
      return data;
    } catch (error) {
      setApprovalFlowError(error.message);
      alert(error.message);
      throw error;
    } finally {
      setApprovalFlowLoading(false);
    }
  };

  const fetchApprovalFlowDataForKey = async (params = {}) => {
    const requestBody = {
      region: params.region,
      businessEntityID: params.businessEntityID,
      reportDate: params.selectedReportDate?.format("YYYY-MM-DD"),
      measure: params.measure,
      dimension: params.dimension,
      dimension2: params.dimension2,
      isGlobal: params.isGlobal,
      isModelerReport: params.isModelerReport,
      isTreasuryKRIReport: params.isTreasuryKRIReport,
      isKRISummaryReport: params.isKRISummaryReport,
      isSFCKRIGlobalSummaryReport: params.isSFCKRIGlobalSummaryReport,
      isRegionKRIGlobalSummaryReport: params.isRegionKRIGlobalSummaryReport,
      isTFSCKRIGlobalSummaryReport: params.isTFSCKRIGlobalSummaryReport,
    };

    const baseKey = buildApprovalFlowKey({
      ...params,
      isRegionKRIGlobalSummaryReport: null,
      isTFSCKRIGlobalSummaryReport: null,
    });

    const regionKey = buildApprovalFlowKey({
      ...params,
      isRegionKRIGlobalSummaryReport: true,
      isTFSCKRIGlobalSummaryReport: null,
    });

    const tfscKey = buildApprovalFlowKey({
      ...params,
      isRegionKRIGlobalSummaryReport: null,
      isTFSCKRIGlobalSummaryReport: true,
    });

    setApprovalFlowLoadingByKey((prev) => ({
      ...prev,
      [baseKey]: true,
      [regionKey]: true,
      [tfscKey]: true,
    }));
    setApprovalFlowErrorByKey((prev) => ({
      ...prev,
      [baseKey]: null,
      [regionKey]: null,
      [tfscKey]: null,
    }));

    try {
      const response = await securedFetch(GET_APPROVAL_FLOW_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) throw new Error("Failed to fetch the approval flow");

      const data = await response.json();
      if (!data) throw new Error("Failed to fetch the approval flow");

      const regionFlow = data.filter(
        (item) => item.regionKRIGlobalSummaryReport === true,
      );

      const tfscFlow = data.filter(
        (item) => item.tfscKRIGlobalSummaryReport === true,
      );

      setApprovalFlowByKey((prev) => ({
        ...prev,
        [baseKey]: data,
        [regionKey]: regionFlow,
        [tfscKey]: tfscFlow,
      }));

      return data;
    } catch (error) {
      setApprovalFlowErrorByKey((prev) => ({
        ...prev,
        [baseKey]: error.message,
        [regionKey]: error.message,
        [tfscKey]: error.message,
      }));
      alert(error.message);
      throw error;
    } finally {
      setApprovalFlowLoadingByKey((prev) => ({
        ...prev,
        [baseKey]: false,
        [regionKey]: false,
        [tfscKey]: false,
      }));
    }
  };

  const sendApproverEmailNotifyForSelectedReport = ({
    region = null,
    businessEntityID = null,
    selectedReportDate = null,
    isGlobal = null,
    isModelerReport = null,
    isTreasuryKRIReport = null,
    isKRISummaryReport = null,
    isSFCKRIGlobalSummaryReport = null,
    isRegionKRIGlobalSummaryReport = null,
    isTFSCKRIGlobalSummaryReport = null,
    measure = null,
    dimension = null,
    dimension2 = null,
  } = {}) => {
    setSendEmailNotifyLoading(true);
    setSendEmailNotifyError(null);
    return securedFetch(SEND_APPROVER_EMAIL_NOTIFY_FOR_SELECTED_REPORT_API, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        region,
        businessEntityID,
        reportDate: selectedReportDate?.format("YYYY-MM-DD"),
        measure,
        dimension,
        dimension2,
        isGlobal,
        isModelerReport,
        isTreasuryKRIReport,
        isKRISummaryReport,
        isSFCKRIGlobalSummaryReport,
        isRegionKRIGlobalSummaryReport,
        isTFSCKRIGlobalSummaryReport,
      }),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("Failed to send the email notification reminder");
        }
        return response.text();
      })
      .then((data) => {
        if (typeof data === "string") {
          setSendEmailNotify(data);
          return data;
        }
        throw new Error("Unexpected Error");
      })
      .catch((error) => {
        setSendEmailNotifyError(error.message);
        alert(error.message);
        throw error;
      })
      .finally(() => {
        setSendEmailNotifyLoading(false);
      });
  };

  const saveApprovalData = ({
    currentApprover,
    type,
    comment,
    reportDate,
  } = {}) => {
    setSaveApprovalLoading(true);
    setSaveApprovalError(null);
    return securedFetch(SAVE_APPROVAL_DATA_API, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        stepApprovalId: currentApprover?.stepApprovalId,
        approvalStatus: type,
        approvalComments: comment,
        reportDate: reportDate?.format("YYYY-MM-DD"),
      }),
    })
      .then((response) => {
        if (!response.ok) throw new Error("Failed to save the approval data");
        return response.text();
      })
      .then((data) => {
        if (typeof data === "string") {
          setSaveApproval(data);
          return data;
        }
        throw new Error("Unexpected response format");
      })
      .catch((error) => {
        setSaveApprovalError(error.message);
        alert(error.message);
        throw error;
      })
      .finally(() => {
        setSaveApprovalLoading(false);
      });
  };

  return (
    <ApprovalContext.Provider
      value={{
        fetchApprovalFlowData,
        fetchApprovalFlowDataForKey,

        approvalFlowLoading,
        approvalFlowError,
        approvalFlow,
        setApprovalFlow,

        approvalFlowByKey,
        approvalFlowLoadingByKey,
        approvalFlowErrorByKey,

        saveApprovalData,
        saveApprovalLoading,
        saveApprovalError,
        saveApproval,

        sendApproverEmailNotifyForSelectedReport,
        sendEmailNotifyLoading,
        sendEmailNotifyError,
        sendEmailNotify,
      }}
    >
      {children}
    </ApprovalContext.Provider>
  );
};

export const useApprovalContext = () => useContext(ApprovalContext);
